'use client';

import { useCallback } from 'react';
import { openDialog } from '@/app/components/shared/imperative-dialog';
import { safeStorage } from '@/app/utils/client';
import { type ConsentType, CreationConsentDialog } from '../_components/CreationConsentDialog';

const CREATION_CONSENT_STORAGE_KEY = 'creation_sensitive_info_consent_v2';

interface CreationConsentConfig {
  requireVoiceConsent?: boolean;
  requireFaceConsent?: boolean;
}

const CONSENT_DIALOG_COPY = {
  title: '生物识别信息授权声明',
  description: '当前功能涉及敏感个人信息处理，提交前需先阅读并同意以下授权书。',
  tip: '请先打开并阅读以下授权书，全部勾选同意后才可继续提交。',
  agreePrefix: '我已阅读并同意',
  cancel: '取消',
  confirm: '已阅读并同意，继续创作',
  voice: {
    linkText: '《敏感个人信息收集处理授权同意书（声纹特征）》',
  },
  face: {
    linkText: '《敏感个人信息收集处理授权同意书（面部识别特征）》',
  },
} as const;

function getCreationConsentStorageKey(userId: string, consentType: ConsentType): string {
  return `${CREATION_CONSENT_STORAGE_KEY}_${consentType}_${userId}`;
}

function getRequiredConsentTypes(config: CreationConsentConfig): ConsentType[] {
  return [
    ...(config.requireVoiceConsent ? (['voice'] as const) : []),
    ...(config.requireFaceConsent ? (['face'] as const) : []),
  ];
}

export function useCreationConsent(userId: string | undefined, config: CreationConsentConfig) {
  return useCallback(async (): Promise<boolean> => {
    const requiredConsentTypes = getRequiredConsentTypes(config);

    if (!userId || requiredConsentTypes.length === 0) {
      return true;
    }

    const pendingConsentTypes = requiredConsentTypes.filter(
      (consentType) => safeStorage.getItem(getCreationConsentStorageKey(userId, consentType)) !== 'true',
    );

    if (pendingConsentTypes.length === 0) {
      return true;
    }

    const consentContentMap: Record<ConsentType, { linkText: string }> = {
      voice: {
        linkText: CONSENT_DIALOG_COPY.voice.linkText,
      },
      face: {
        linkText: CONSENT_DIALOG_COPY.face.linkText,
      },
    };

    return new Promise<boolean>((resolve) => {
      let isResolved = false;
      let closeDialog = () => {};

      const finish = (result: boolean) => {
        if (isResolved) {
          return;
        }
        isResolved = true;
        resolve(result);
      };

      const dialog = openDialog({
        title: CONSENT_DIALOG_COPY.title,
        description: CONSENT_DIALOG_COPY.description,
        className: 'w-[560px] sm:w-[calc(100%-24px)]',
        content: (
          <CreationConsentDialog
            consentTypes={pendingConsentTypes}
            dialogTip={CONSENT_DIALOG_COPY.tip}
            agreePrefix={CONSENT_DIALOG_COPY.agreePrefix}
            cancelText={CONSENT_DIALOG_COPY.cancel}
            confirmText={CONSENT_DIALOG_COPY.confirm}
            consentContentMap={consentContentMap}
            onCancel={() => {
              finish(false);
              closeDialog();
            }}
            onConfirm={(agreedConsentTypes) => {
              agreedConsentTypes.forEach((consentType) => {
                safeStorage.setItem(getCreationConsentStorageKey(userId, consentType), 'true');
              });
              finish(true);
              closeDialog();
            }}
          />
        ),
        onClose: () => {
          finish(false);
        },
      });

      closeDialog = dialog.close;
    });
  }, [config, userId]);
}
